/**
 * TimelineCanvas Component
 *
 * Renders all segments on a single timeline row.
 * Handles:
 * - Segment positioning based on dates AND transform (zoom/pan)
 * - Today marker
 * - Background segments (future feature)
 */

import { useMemo, useRef } from 'react'
import { TimelineSegment } from './TimelineSegment'
import { TimelineTodayMarker } from './TimelineTodayMarker'
import type { TimelineSegment as TimelineSegmentType, TimelineViewportProps, TimelineTransform } from '../types'
import { useTimelineZoom } from '../hooks/useTimelineZoom'

interface TimelineCanvasProps extends Omit<TimelineViewportProps, 'label'> {
  /** Timeline domain bounds */
  domain: {
    minDate: number
    maxDate: number
  }
  /** Container width in pixels */
  containerWidth: number
  /** Current zoom/pan transform */
  transform: TimelineTransform
  /** Zoom constraints */
  constraints: {
    minScale: number
    maxScale: number
    zoomStep: number
  }
  /** Callback when transform changes (from interactive zoom/pan) */
  onTransformChange: (transform: TimelineTransform) => void
  /** Currently hovered segment UID (for bidirectional highlighting) */
  hoveredSegmentUid?: string | null
  /** Callback when segment hover state changes */
  onSegmentHover?: (uid: string | null) => void
}

/**
 * Calculate segment position in pixels, applying transform
 *
 * NOTE: We intentionally do NOT enforce a minimum width here.
 * Very narrow segments (e.g., 1-day records on a long timeline) will render
 * at their true proportional width. This ensures:
 * - No artificial overlap between adjacent segments
 * - No visual "resizing" when z-index changes on selection
 * - Accurate representation of time spans
 *
 * Narrow segments are still interactive (CSS ensures minimum click target)
 * and have tooltips for identification.
 */
function calculateSegmentPosition(
  segment: TimelineSegmentType,
  minDate: number,
  maxDate: number,
  containerWidth: number,
  transform: TimelineTransform
): { left: number; width: number } {
  const totalSpan = maxDate - minDate

  const startTimestamp = segment.effectiveFrom
    ? new Date(segment.effectiveFrom).getTime()
    : minDate

  const endTimestamp = segment.effectiveTo
    ? new Date(segment.effectiveTo).getTime()
    : maxDate

  // Calculate base positions (0 to containerWidth range)
  const baseLeft = ((startTimestamp - minDate) / totalSpan) * containerWidth
  const baseRight = ((endTimestamp - minDate) / totalSpan) * containerWidth
  const baseWidth = baseRight - baseLeft

  // Apply transform: scale and translate
  const scaledLeft = baseLeft * transform.k + transform.x
  const scaledWidth = baseWidth * transform.k

  // Ensure at least 1px width so the segment is visible (but no artificial expansion)
  const finalWidth = Math.max(scaledWidth, 1)

  return {
    left: scaledLeft,
    width: finalWidth,
  }
}

/**
 * TimelineCanvas Component
 */
export function TimelineCanvas({
  segments,
  backgroundSegments = [],
  height,
  showBackgroundSegments = false,
  onSegmentClick,
  selectedSegmentUid,
  hoveredSegmentUid,
  onSegmentHover,
  domain,
  containerWidth,
  transform,
  constraints,
  onTransformChange,
}: TimelineCanvasProps) {
  const { minDate, maxDate } = domain
  const totalSpan = maxDate - minDate
  const canvasRef = useRef<HTMLDivElement>(null)

  // Attach interactive zoom/pan behavior
  useTimelineZoom({
    containerRef: canvasRef,
    transform,
    onTransformChange,
    constraints,
    containerWidth,
    enabled: true,
  })

  // Calculate today marker position (applying transform)
  const todayPosition = useMemo(() => {
    const now = Date.now()
    const basePosition = ((now - minDate) / totalSpan) * containerWidth
    const scaledPosition = basePosition * transform.k + transform.x

    // Convert to percentage of container width
    const positionPercent = (scaledPosition / containerWidth) * 100

    // Only show if within visible bounds (with some margin)
    return positionPercent > -5 && positionPercent < 105 ? positionPercent : null
  }, [minDate, totalSpan, containerWidth, transform])

  // Sort segments by zIndex for proper layering
  const sortedSegments = useMemo(() => {
    return [...segments].sort((a, b) => (a.zIndex ?? 1) - (b.zIndex ?? 1))
  }, [segments])

  return (
    <div
      ref={canvasRef}
      className="timeline-canvas"
      style={{ height }}
    >
      {/* Background segments (if enabled) */}
      {showBackgroundSegments && backgroundSegments.map((segment) => {
        const { left, width } = calculateSegmentPosition(
          segment,
          minDate,
          maxDate,
          containerWidth,
          transform
        )

        // Skip segments that are completely outside the visible area
        if (left + width < -50 || left > containerWidth + 50) return null

        // Background segments are clickable/selectable just like main segments
        const isSelected = segment.uid === selectedSegmentUid
        const isHovered = segment.uid === hoveredSegmentUid

        return (
          <TimelineSegment
            key={`bg-${segment.uid}`}
            segment={{ ...segment, familyIndex: undefined }}
            left={left}
            width={width}
            isSelected={isSelected}
            isHovered={isHovered}
            onClick={
              segment.isSelectable !== false && onSegmentClick
                ? () => onSegmentClick(segment)
                : undefined
            }
            onHover={onSegmentHover}
          />
        )
      })}

      {/* Main segments */}
      {sortedSegments.map((segment) => {
        const { left, width } = calculateSegmentPosition(
          segment,
          minDate,
          maxDate,
          containerWidth,
          transform
        )

        // Skip segments that are completely outside the visible area
        if (left + width < -50 || left > containerWidth + 50) return null

        // Check for selection match - handles continuation UID variants
        // When a continuation row is selected (uid = "continuation-xyz"),
        // also highlight the source segment in BEFORE timeline (uid = "xyz")
        const isSelected = segment.uid === selectedSegmentUid ||
          (!!selectedSegmentUid?.startsWith('continuation-') &&
           segment.uid === selectedSegmentUid.replace('continuation-', ''))

        // Check for hover match - same logic for continuation variants
        const isHovered = segment.uid === hoveredSegmentUid ||
          (!!hoveredSegmentUid?.startsWith('continuation-') &&
           segment.uid === hoveredSegmentUid.replace('continuation-', ''))

        return (
          <TimelineSegment
            key={segment.uid}
            segment={segment}
            left={left}
            width={width}
            isSelected={isSelected}
            isHovered={isHovered}
            onClick={
              segment.isSelectable !== false && onSegmentClick
                ? () => onSegmentClick(segment)
                : undefined
            }
            onHover={onSegmentHover}
          />
        )
      })}

      {/* Today marker */}
      {todayPosition !== null && (
        <TimelineTodayMarker position={todayPosition} />
      )}
    </div>
  )
}

export default TimelineCanvas
